import { forwardRef } from "react";
import { Check, Sparkles, Zap } from "lucide-react";
import { cn } from "@/lib/cn";
import { Badge } from "./Badge";

/**
 * PlanCard — billing/tariff sahifasida plan kartasi (Faza 8).
 *
 * Variantlar:
 *   - default: oddiy karta
 *   - current: hozirgi plan (gradient border + glow)
 *   - featured: tavsiya etilgan (Aurora ribbon top)
 *
 * Use-case:
 *   - Billing dashboard'da 3 ta plan ko'rsatish (Free / Pro / Enterprise)
 *   - PRO featured (most popular)
 *
 * UX:
 *   - Featured plan tepasida "Tavsiya etiladi" gradient ribbon
 *   - Current plan'da "Joriy reja" badge + glow
 *   - Feature list — Check ikonlar (yoki — Cross variantlar uchun)
 *   - Primary CTA — gradient (active CTA)
 *
 * Token'lar: --plancard-* (tokens.css)
 */
export type PlanTier = "free" | "pro" | "enterprise";

export interface PlanFeature {
  text: string;
  included?: boolean; // default true (✓ icon)
  highlight?: boolean; // qalin ko'rsatish
}

export interface PlanCardProps {
  tier: PlanTier;
  /** Plan nomi (default: tier label) */
  name?: string;
  /** Tagline (subtitle) */
  description?: string;
  /** Narx (formatted: "299 000 so'm/oy" yoki "Bepul" yoki "Aloqaga chiqing") */
  price: string;
  /** Eski narx (chiziq tortilgan) */
  oldPrice?: string;
  /** Featurelar */
  features: PlanFeature[];
  /** Joriy reja ekanligi */
  current?: boolean;
  /** "Tavsiya etiladi" / "Eng mashhur" ribbon */
  featured?: boolean;
  /** Variant: featured ribbon matni */
  featuredLabel?: string;
  /** CTA tugma matni */
  ctaLabel?: string;
  /** CTA bosilganda */
  onCtaClick?: () => void;
  /** CTA disabled */
  ctaDisabled?: boolean;
  /** CTA loading */
  ctaLoading?: boolean;
  className?: string;
}

const TIER_META: Record<
  PlanTier,
  { name: string; ribbon: string; accentSoft: string; iconColor: string }
> = {
  free: {
    name: "Free",
    ribbon: "var(--plancard-ribbon-free)",
    accentSoft: "transparent",
    iconColor: "var(--text-secondary)",
  },
  pro: {
    name: "Pro",
    ribbon: "var(--plancard-ribbon-pro)",
    accentSoft: "var(--accent-violet-soft)",
    iconColor: "var(--accent-violet)",
  },
  enterprise: {
    name: "Enterprise",
    ribbon: "var(--plancard-ribbon-enterprise)",
    accentSoft: "var(--accent-cyan-soft)",
    iconColor: "var(--accent-cyan)",
  },
};

export const PlanCard = forwardRef<HTMLDivElement, PlanCardProps>(
  (
    {
      tier,
      name,
      description,
      price,
      oldPrice,
      features,
      current = false,
      featured = false,
      featuredLabel = "Eng mashhur",
      ctaLabel,
      onCtaClick,
      ctaDisabled,
      ctaLoading,
      className,
    },
    ref,
  ) => {
    const meta = TIER_META[tier];
    const displayName = name ?? meta.name;
    const defaultCtaLabel =
      current ? "Joriy reja" : tier === "enterprise" ? "Aloqaga chiqing" : "Tanlash";
    const cta = ctaLabel ?? defaultCtaLabel;

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex flex-col rounded-2xl border p-5 transition-all duration-150",
          current
            ? "border-[var(--plancard-current-border)] bg-[var(--plancard-current-bg)] shadow-[var(--plancard-current-glow)]"
            : featured
              ? "border-[var(--plancard-featured-border)] bg-[var(--plancard-bg)] shadow-[var(--plancard-featured-glow)]"
              : "border-[var(--plancard-border)] bg-[var(--plancard-bg)] hover:border-[var(--plancard-border-hover)]",
          className,
        )}
        style={
          featured
            ? { backgroundImage: "var(--plancard-featured-bg)" }
            : undefined
        }
      >
        {/* Ribbon (top-right corner gradient strip) */}
        {(featured || current) && (
          <div
            className={cn(
              "absolute -top-2.5 right-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white",
              "shadow-[var(--shadow-soft)]",
            )}
            style={{
              background: current
                ? "var(--accent-gradient)"
                : meta.ribbon,
            }}
          >
            {current ? (
              <>
                <Check className="h-3 w-3" aria-hidden />
                <span>Joriy reja</span>
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" aria-hidden />
                <span>{featuredLabel}</span>
              </>
            )}
          </div>
        )}

        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">{displayName}</h3>
            {tier === "pro" && (
              <Badge size="sm" variant="plan-pro" dot>
                Pro
              </Badge>
            )}
            {tier === "enterprise" && (
              <Badge size="sm" variant="plan-enterprise" dot>
                Enterprise
              </Badge>
            )}
          </div>
          {description && (
            <p className="text-xs text-[var(--text-secondary)] leading-snug">{description}</p>
          )}
        </div>

        {/* Price */}
        <div className="mb-5 flex items-baseline gap-2">
          {oldPrice && (
            <span className="text-sm text-[var(--text-muted)] line-through">{oldPrice}</span>
          )}
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              {price}
            </span>
          </div>
        </div>

        {/* Feature list */}
        <ul className="flex-1 flex flex-col gap-2 mb-5">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              {f.included !== false ? (
                <Check
                  className="h-4 w-4 mt-0.5 shrink-0"
                  style={{ color: meta.iconColor }}
                  aria-hidden
                />
              ) : (
                <span
                  aria-hidden
                  className="mt-1.5 inline-block h-1 w-3 rounded-full bg-[var(--text-muted)]/40 shrink-0"
                />
              )}
              <span
                className={cn(
                  "text-sm leading-snug",
                  f.included === false
                    ? "text-[var(--text-muted)] line-through"
                    : f.highlight
                      ? "text-[var(--text-primary)] font-medium"
                      : "text-[var(--text-secondary)]",
                )}
              >
                {f.text}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button
          type="button"
          onClick={onCtaClick}
          disabled={ctaDisabled || current || ctaLoading}
          className={cn(
            "h-10 w-full inline-flex items-center justify-center gap-2 rounded-lg font-medium text-sm transition-all duration-150",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-base)]",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            current
              ? "border border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-secondary)]"
              : featured || tier === "pro" || tier === "enterprise"
                ? "text-white shadow-[var(--shadow-soft)] hover:shadow-[var(--glow-sm)] hover:brightness-110 active:brightness-95"
                : "border border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)]",
          )}
          style={
            !current && (featured || tier === "pro" || tier === "enterprise")
              ? { background: "var(--accent-gradient)" }
              : undefined
          }
        >
          {ctaLoading && (
            <span
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
              aria-hidden
            />
          )}
          {!ctaLoading && (featured || tier === "enterprise") && (
            <Zap className="h-3.5 w-3.5" aria-hidden />
          )}
          <span>{cta}</span>
        </button>
      </div>
    );
  },
);
PlanCard.displayName = "PlanCard";
